import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Sparkles, FileText, Image as ImageIcon, X, Loader2, CheckCircle2, AlertCircle, Trash2, Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIE = [
  'tvcc', 'allarmi', 'domotica', 'frutti_elettrici', 'automazione',
  'illuminazione', 'cavi', 'quadri', 'reti_lan', 'audio_video',
  'climatizzazione', 'rivelazione_incendio', 'altro',
];

const UM_OPTIONS = ['pz', 'm', 'kg', 'conf', 'scatola', 'matassa', 'rotolo', 'kit', 'l'];

const MATCH_STATUS_LABELS = {
  new: { label: 'Nuovo', class: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  existing_code: { label: 'Esistente (codice)', class: 'bg-amber-50 text-amber-700 border-amber-200' },
  existing_desc: { label: 'Forse esistente', class: 'bg-blue-50 text-blue-700 border-blue-200' },
};

export default function ListinoOcrDialog({ open, onOpenChange, fornitore }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState('upload'); // 'upload' | 'review' | 'imported'
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [selected, setSelected] = useState({}); // {idx: true}
  const [categoriaDefault, setCategoriaDefault] = useState('altro');
  const [applyToExisting, setApplyToExisting] = useState(true);
  const [importResult, setImportResult] = useState(null);

  const reset = () => {
    setFile(null);
    setRows([]);
    setMeta(null);
    setSelected({});
    setPhase('upload');
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) {
      toast.error('File troppo grande (max 15 MB)');
      return;
    }
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowed.includes(f.type)) {
      toast.error('Formato non supportato. Usa PDF, JPG, PNG o WEBP.');
      return;
    }
    setFile(f);
  };

  const runOcr = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('categoria_hint', categoriaDefault);
      const r = await axios.post(`${API}/listini-fornitore/ocr`, fd, {
        withCredentials: true,
        timeout: 180000,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const extracted = (r.data?.righe || []).map((row, i) => ({ _idx: i, ...row }));
      setRows(extracted);
      setMeta({
        totale: r.data?.totale_righe || 0,
        stimato: r.data?.totale_righe_stimato || null,
        fornitore_rilevato: r.data?.fornitore_rilevato || '',
        filename: r.data?.filename || file.name,
      });
      // Seleziona di default solo le righe con prezzo e descrizione
      const initial = {};
      extracted.forEach(row => {
        if (row.descrizione && row.prezzo_unitario != null) initial[row._idx] = true;
      });
      setSelected(initial);
      setPhase('review');
      if (extracted.length === 0) {
        toast.warning('Nessuna riga estratta dal file.');
      } else {
        toast.success(`Estratte ${extracted.length} righe — rivedi prima dell'import`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore OCR');
    } finally {
      setBusy(false);
    }
  };

  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map(r => r._idx === idx ? { ...r, [field]: value } : r));
  };

  const removeRow = (idx) => {
    setRows(prev => prev.filter(r => r._idx !== idx));
    setSelected(prev => { const c = { ...prev }; delete c[idx]; return c; });
  };

  const addEmptyRow = () => {
    setRows(prev => {
      const newIdx = (prev.reduce((m, r) => Math.max(m, r._idx), -1)) + 1;
      const newRow = { _idx: newIdx, codice: '', descrizione: '', prezzo_unitario: null, unita_misura: 'pz', match_status: 'new' };
      setSelected(s => ({ ...s, [newIdx]: true }));
      return [...prev, newRow];
    });
  };

  const toggleAll = (val) => {
    if (val) {
      const all = {};
      rows.forEach(r => { all[r._idx] = true; });
      setSelected(all);
    } else {
      setSelected({});
    }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const runImport = async () => {
    if (selectedCount === 0) {
      toast.error('Seleziona almeno una riga');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        fornitore_id: fornitore.id,
        fornitore_nome: fornitore.ragione_sociale,
        categoria_default: categoriaDefault,
        apply_to_existing: applyToExisting,
        save_history: true,
        source_filename: meta?.filename || '',
        rows: rows
          .filter(r => selected[r._idx] && r.descrizione?.trim())
          .map(r => ({
            codice: r.codice || '',
            descrizione: r.descrizione,
            prezzo_unitario: r.prezzo_unitario != null ? Number(r.prezzo_unitario) : null,
            unita_misura: r.unita_misura || 'pz',
            quantita_confezione: r.quantita_confezione != null ? Number(r.quantita_confezione) : null,
            sconto_pct: r.sconto_pct != null ? Number(r.sconto_pct) : null,
            note: r.note || '',
            existing_id: r.existing_id || null,
            match_status: r.match_status || 'new',
          })),
      };
      const r = await axios.post(`${API}/listini-fornitore/import`, payload, { withCredentials: true });
      setImportResult(r.data);
      setPhase('imported');
      toast.success(`Importato: ${r.data.inserted} nuovi · ${r.data.updated} aggiornati`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore import');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[1100px] max-h-[92vh] overflow-y-auto" data-testid="listino-ocr-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            Importa listino — {fornitore?.ragione_sociale}
          </DialogTitle>
          <DialogDescription>
            Carica il listino del fornitore (PDF o foto), l'AI estrae automaticamente codici, descrizioni e prezzi.
            Rivedi le righe, deseleziona quelle errate e importa nel catalogo.
          </DialogDescription>
        </DialogHeader>

        {/* PHASE 1: UPLOAD */}
        {phase === 'upload' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Categoria di default per gli articoli nuovi</Label>
                <Select value={categoriaDefault} onValueChange={setCategoriaDefault}>
                  <SelectTrigger data-testid="ocr-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIE.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1">Puoi cambiarla riga per riga in fase di review.</p>
              </div>
              <div className="flex items-end">
                <div className="text-xs text-slate-500 leading-relaxed bg-slate-50 p-2 rounded border">
                  <b>Formati accettati</b>: PDF (multi-pagina), JPG, PNG, WEBP.<br />
                  <b>Dimensione max</b>: 15 MB. Tempo medio: 10-30 sec.
                </div>
              </div>
            </div>

            {!file ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-slate-300 rounded-lg p-12 text-center hover:border-purple-400 hover:bg-purple-50/30 transition-colors group"
                data-testid="ocr-dropzone"
              >
                <Upload className="h-10 w-10 mx-auto text-slate-400 group-hover:text-purple-500 mb-3" />
                <p className="text-sm font-medium text-slate-700">Clicca per selezionare un file</p>
                <p className="text-xs text-slate-500 mt-1">o trascina qui un PDF / immagine</p>
              </button>
            ) : (
              <div className="border rounded-lg p-4 bg-slate-50 flex items-center gap-3" data-testid="ocr-file-preview">
                {file.type === 'application/pdf' ? (
                  <FileText className="h-10 w-10 text-red-500 flex-shrink-0" />
                ) : (
                  <ImageIcon className="h-10 w-10 text-blue-500 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{file.name}</p>
                  <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB · {file.type}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} data-testid="ocr-file-remove">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept=".pdf,image/jpeg,image/png,image/webp,image/jpg" onChange={handleFile} className="hidden" />

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => handleClose(false)}>Annulla</Button>
              <Button
                onClick={runOcr}
                disabled={!file || busy}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="ocr-run-btn"
              >
                {busy ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Estrazione in corso…</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" />Estrai listino con AI</>
                )}
              </Button>
            </div>

            {busy && (
              <p className="text-xs text-slate-500 italic text-center">
                L'AI sta leggendo il file e estraendo le righe. Per PDF multi-pagina può richiedere fino a 60 secondi.
              </p>
            )}
          </div>
        )}

        {/* PHASE 2: REVIEW */}
        {phase === 'review' && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-slate-50 rounded-lg border text-xs">
              <div>
                <p className="text-slate-500">Estratte</p>
                <p className="font-bold text-lg text-slate-900">{rows.length}</p>
              </div>
              <div>
                <p className="text-slate-500">Selezionate</p>
                <p className="font-bold text-lg text-purple-700" data-testid="selected-count">{selectedCount}</p>
              </div>
              <div>
                <p className="text-slate-500">Già in catalogo</p>
                <p className="font-bold text-lg text-amber-700">{rows.filter(r => r.existing_id).length}</p>
              </div>
              <div>
                <p className="text-slate-500">Nuovi</p>
                <p className="font-bold text-lg text-emerald-700">{rows.filter(r => !r.existing_id).length}</p>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-xs">
                <Checkbox
                  checked={selectedCount === rows.length && rows.length > 0}
                  onCheckedChange={toggleAll}
                  data-testid="ocr-select-all"
                />
                <span className="text-slate-600">Seleziona tutte</span>
                <span className="mx-2 text-slate-300">|</span>
                <Checkbox checked={applyToExisting} onCheckedChange={setApplyToExisting} data-testid="ocr-apply-existing" />
                <span className="text-slate-600">Aggiorna prezzi articoli già in catalogo</span>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">Categoria default:</Label>
                <Select value={categoriaDefault} onValueChange={setCategoriaDefault}>
                  <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIE.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={addEmptyRow} data-testid="ocr-add-row"><Plus className="h-3.5 w-3.5 mr-1" />Riga</Button>
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-10"></TableHead>
                    <TableHead className="w-32">Codice</TableHead>
                    <TableHead>Descrizione</TableHead>
                    <TableHead className="w-28 text-right">Prezzo €</TableHead>
                    <TableHead className="w-20">U.M.</TableHead>
                    <TableHead className="w-32">Stato</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-6">Nessuna riga — riprova con un file più nitido.</TableCell></TableRow>
                  )}
                  {rows.map(r => {
                    const status = MATCH_STATUS_LABELS[r.match_status || 'new'];
                    return (
                      <TableRow key={r._idx} className={selected[r._idx] ? 'bg-purple-50/30' : ''} data-testid={`ocr-row-${r._idx}`}>
                        <TableCell>
                          <Checkbox
                            checked={!!selected[r._idx]}
                            onCheckedChange={(v) => setSelected(s => ({ ...s, [r._idx]: !!v }))}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={r.codice || ''}
                            onChange={(e) => updateRow(r._idx, 'codice', e.target.value)}
                            className="h-7 text-xs font-mono"
                            placeholder="Codice"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={r.descrizione || ''}
                            onChange={(e) => updateRow(r._idx, 'descrizione', e.target.value)}
                            className="h-7 text-xs"
                            placeholder="Descrizione articolo"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            value={r.prezzo_unitario ?? ''}
                            onChange={(e) => updateRow(r._idx, 'prezzo_unitario', e.target.value ? parseFloat(e.target.value) : null)}
                            className="h-7 text-xs text-right font-mono"
                            placeholder="0.00"
                          />
                          {r.existing_id && r.existing_prezzo_acquisto != null && (
                            <p className="text-[10px] text-amber-600 mt-0.5 text-right">
                              Era: € {Number(r.existing_prezzo_acquisto).toFixed(2)}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Select value={r.unita_misura || 'pz'} onValueChange={(v) => updateRow(r._idx, 'unita_misura', v)}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>{UM_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] ${status.class}`}>{status.label}</Badge>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => removeRow(r._idx)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="flex justify-between items-center pt-2 border-t">
              <Button variant="ghost" onClick={() => { setPhase('upload'); setRows([]); }} data-testid="ocr-back">
                ← Ricarica file
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => handleClose(false)}>Annulla</Button>
                <Button
                  onClick={runImport}
                  disabled={busy || selectedCount === 0}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="ocr-import-btn"
                >
                  {busy ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Import in corso…</>
                  ) : (
                    <><CheckCircle2 className="h-4 w-4 mr-2" />Importa {selectedCount} articoli</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* PHASE 3: IMPORTED */}
        {phase === 'imported' && importResult && (
          <div className="space-y-4" data-testid="ocr-result">
            <div className="text-center py-6">
              <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-slate-900">Import completato</h3>
              <p className="text-sm text-slate-500 mt-1">I prezzi sono ora disponibili in Catalogo Materiali</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-emerald-700">{importResult.inserted}</p>
                <p className="text-xs text-emerald-600 mt-1">Nuovi articoli</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-amber-700">{importResult.updated}</p>
                <p className="text-xs text-amber-600 mt-1">Prezzi aggiornati</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-slate-700">{importResult.skipped}</p>
                <p className="text-xs text-slate-600 mt-1">Saltati</p>
              </div>
            </div>
            {importResult.errors?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-red-700 text-sm font-semibold mb-1">
                  <AlertCircle className="h-4 w-4" />
                  {importResult.total_errors} errori
                </div>
                <ul className="text-xs text-red-600 space-y-0.5 list-disc list-inside">
                  {importResult.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={reset}>Carica un altro listino</Button>
              <Button onClick={() => handleClose(false)} className="bg-slate-900 text-white">Chiudi</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
